import React, {
  TextareaHTMLAttributes,
  useCallback,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { useAutoHeight, useForwardedRef } from '@adsk/alloy-react-helpers';
import theme, {
  getInteractiveStyles,
  stylePropType,
  DetailedStylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

const StyledCount = styledC.div<{
  disabled?: boolean;
  $error?: boolean;
}>`
  color: ${theme.tokens.colors.text.secondary.value};
  pointer-events: none;
  position: absolute;
  right: ${theme.spacing.XXS}px;
  bottom: ${theme.spacing.XXS}px;

  ${(props) => props.disabled && `color: ${theme.tokens.colors.text.inactive};`}
  ${(props) =>
    props.$error && `color: ${theme.tokens.colors.text.error.value};`}
`;

export type TextareaProps = DetailedStylableComponent<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
> & {
  error?: boolean;
  disabled?: boolean;
  maxHeight?: number | string;
  minHeight?: number | string;
  value?: string;
  preventAutoHeight?: boolean;
  softLimit?: number;
  showCountThreshold?: number;
  showCount?: boolean;
  inputStyle?: StyleProp;
  inputClassName?: string;
};
/**
 * Textarea component that expands with text content.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to HTML textarea element.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      style,
      className,
      error = false,
      disabled = false,
      maxHeight = '100%',
      minHeight = 36,
      onChange,
      value,
      preventAutoHeight,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      defaultValue,
      showCount,
      inputStyle,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const textareaRef = useForwardedRef(ref);
    const [height, updateHeight] = useAutoHeight(textareaRef);
    /**
     * NOTE(anderson): This is only required because the textarea had an important height set
     * which we are not ready to remove now, because users of textarea rely on it (CommentInput and others)
     */
    const updateImportantHeight = useCallback(() => {
      textareaRef.current?.style.setProperty('height', '0', 'important');
      updateHeight();
      textareaRef.current?.style.setProperty('height', '100%', 'important');
    }, [textareaRef, updateHeight]);
    const [charCount, setCharCount] = useState(0);
    const limit = useMemo(
      () => showCount !== false && (softLimit || maxLength),
      [showCount, softLimit, maxLength],
    );

    const showLimit = useMemo(
      () =>
        limit &&
        (!showCountThreshold ||
          (showCountThreshold && limit - charCount <= showCountThreshold)),
      [limit, showCountThreshold, charCount],
    );

    useLayoutEffect(() => {
      /**
       * NOTE(anderson): Since we're now controlling the height above,
       * we need to set 100% important height first if user wants to prevent auto height
       */
      textareaRef.current?.style.setProperty('height', '100%', 'important');

      if (!preventAutoHeight) {
        updateImportantHeight();
      }
    }, [preventAutoHeight, textareaRef, updateImportantHeight]);

    useLayoutEffect(() => {
      if (value !== undefined && value !== null) {
        setCharCount(value.length);
      }
    }, [value]);

    useLayoutEffect(() => {
      if (defaultValue !== undefined && defaultValue !== null) {
        setCharCount(defaultValue.toString().length);
      }
      // we only want to do this on mount since it's the default value
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (value === undefined || value === null) {
          if (event.target.value) {
            setCharCount(event.target.value.length);
          } else {
            setCharCount(0);
          }
        }

        updateImportantHeight();
        onChange?.(event);
      },
      [onChange, updateImportantHeight, value],
    );

    return (
      <div
        className={className}
        css={[
          {
            ...theme.typography.labelMedium,
            color: theme.tokens.colors.text.primary.value,
            border: `1px solid ${theme.tokens.colors.border.value}`,
            borderRadius: theme.borderRadius.small,
            boxSizing: 'border-box',
            height,
            maxHeight,
            minHeight,
            position: 'relative',
            margin: 0,
            padding: 0,

            ...getInteractiveStyles(disabled),

            ...(disabled && {
              background: theme.tokens.colors.background.neutral.inactive.value,
              borderColor: theme.tokens.colors.border.inactive.value,
              boxShadow: 'none',
              color: theme.tokens.colors.text.inactive.value,
              cursor: 'not-allowed',
            }),

            ...(!error && {
              '&:focus:not(:active)': {
                borderColor: theme.tokens.colors.border.focused.value,
              },
            }),

            ...(error && {
              borderColor: theme.tokens.colors.border.error.value,
            }),
            ...(showLimit && {
              paddingBottom: 28,
            }),
          },
          style,
        ]}
        data-testid="Textarea__wrapper"
      >
        <textarea
          css={[
            {
              outline: 'none',
              resize: 'none',
              border: 'none',
              overflow: 'auto',
              width: '100%',
              padding: `${theme.spacing.XS}px ${theme.spacing.S}px`,

              ...(!showLimit && getInteractiveStyles(disabled)),

              '&::placeholder': {
                color: theme.tokens.colors.text.tertiary.value,
              },

              '&:disabled': {
                background:
                  theme.tokens.colors.background.neutral.inactive.value,
                color: theme.tokens.colors.text.inactive.value,
                cursor: 'not-allowed',
                boxShadow: 'none',
              },
            },
            inputStyle,
          ]}
          className={inputClassName}
          disabled={disabled}
          ref={textareaRef}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          maxLength={maxLength}
          {...props}
        />
        {!!showLimit && (
          <StyledCount
            disabled={disabled}
            $error={error}
            data-testid="Textarea__count"
          >
            {`${charCount}/${limit}`}
          </StyledCount>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Max height in pixels or percent (applied via CSS) */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Min height in pixels or percent (applied via CSS) */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** String value of the textarea */
  value: PropTypes.string,
  /** Function executed on text content change */
  onChange: PropTypes.func,
  /** Text to display when input is empty */
  placeholder: PropTypes.string,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** Styles applied to the input element */
  inputStyle: stylePropType,
  /** Class applied to the input element */
  inputClassName: PropTypes.string,
  /** If true, prevents the textarea from automatically resizing based on content */
  preventAutoHeight: PropTypes.bool,
};

export default Textarea;
